import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertOctagon, Flame, Activity, Globe } from 'lucide-react';
import { adminApi as api } from '@/lib/api';
import { useLiveChannel } from '@/lib/live';
import DashboardShell from '@/components/enterprise/DashboardShell';
import { Panel, Kpi, StatusPill, ConnectionState } from '@/components/enterprise/ui';
import { Donut, Bars } from '@/components/enterprise/charts';

const SEV_COLOR = { low: '#7E8AA3', medium: '#FFCC66', high: '#FF8A5C', critical: '#FF5C7A' };

export default function SecurityDashboard() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState([]);
  const [kpis, setKpis] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/dashboards/security').then((r) => {
      setSnap(r.data);
      if (r.data.status === 'connected' && r.data.data) { setFeed(r.data.data.events); setKpis(r.data.data.kpis); }
    }).catch(() => setSnap({ status: 'offline', source: 'security' })).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onTick = useCallback((msg) => {
    if (!msg || !msg.data) return;
    if (msg.data.kpis) setKpis(msg.data.kpis);
    if (msg.data.events && msg.data.events.length) setFeed((f) => [...msg.data.events, ...f].slice(0, 16));
  }, []);
  useLiveChannel('security', onTick);

  const ok = snap && snap.status === 'connected' && snap.data;
  const d = ok ? snap.data : null;
  const k = kpis || (d && d.kpis);
  const severitySegments = d ? Object.entries(d.severity_dist).map(([s, v]) => ({ value: v, color: SEV_COLOR[s] || '#7E8AA3', label: s })) : [];

  return (
    <DashboardShell title="Cybersecurity Operations Center" subtitle={ok ? `SOC / SIEM · wazuh · source: ${snap.source}` : 'soc / siem'}>
      {loading && <div className="text-sm text-[color:var(--itw-text-3)] font-mono">loading SOC…</div>}
      {!loading && !ok && <Panel><ConnectionState status={snap ? snap.status : 'offline'} source={snap ? snap.source : 'security'} onRetry={load} /></Panel>}
      {ok && k && (
        <div className="space-y-6" data-testid="security-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Threats blocked" value={k.threats_blocked.toLocaleString('de-DE')} sub="total" accent="#2EF2A5" testid="soc-kpi-blocked" />
            <Kpi label="Events / min" value={k.events_min} accent="#00E7FF" />
            <Kpi label="Critical open" value={k.critical_open} accent="#FF5C7A" testid="soc-kpi-critical" />
            <Kpi label="Auth failures" value={k.auth_failures} accent="#FFCC66" />
            <Kpi label="Security score" value={k.security_score} unit="/100" accent="#2EF2A5" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Panel title="Live Threat Feed" subtitle="real-time events" status="connected" className="lg:col-span-2" testid="soc-feed">
              <div className="space-y-1.5 max-h-[360px] overflow-hidden">
                {feed.map((e) => (
                  <motion.div key={e.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}
                    className="flex items-center gap-2 rounded-lg border border-[color:var(--itw-border)] px-3 py-2 font-mono text-[12px]">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: SEV_COLOR[e.severity] }} />
                    <span className="text-[color:var(--itw-text-3)] w-8 shrink-0">{e.cc}</span>
                    <span className="text-[color:var(--itw-text)] w-24 shrink-0 truncate">{e.city}</span>
                    <span className="text-[color:var(--itw-text-2)] flex-1 truncate">{e.vector}</span>
                    <span className="text-[color:var(--itw-text-3)] hidden sm:inline">{e.ip}</span>
                    <StatusPill status={e.severity} label={e.action} />
                  </motion.div>
                ))}
              </div>
            </Panel>
            <Panel title="Severity Distribution" subtitle="last 24h" testid="soc-severity">
              <div className="flex flex-col items-center">
                <Donut segments={severitySegments} />
                <div className="mt-4 w-full space-y-1.5">
                  {severitySegments.map((s) => (
                    <div key={s.label} className="flex items-center justify-between text-[12px]">
                      <span className="flex items-center gap-2 text-[color:var(--itw-text-2)] capitalize"><span className="h-2 w-2 rounded-full" style={{ background: s.color }} />{s.label}</span>
                      <span className="font-mono text-[color:var(--itw-text)]">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Panel title="Event Timeline" subtitle="events / 30min" className="lg:col-span-2" testid="soc-timeline">
              <Bars data={d.timeline} color="#00E7FF" height={90} />
            </Panel>
            <Panel title="Firewall" subtitle="edge" testid="soc-firewall">
              <div className="space-y-3 font-mono text-[13px]">
                <div className="flex justify-between"><span className="text-[color:var(--itw-text-3)]">Rules</span><span>{d.firewall.rules}</span></div>
                <div className="flex justify-between"><span className="text-[color:var(--itw-text-3)]">Drops</span><span className="text-[#FF5C7A]">{d.firewall.drops.toLocaleString('de-DE')}</span></div>
                <div className="flex justify-between"><span className="text-[color:var(--itw-text-3)]">Allowed</span><span className="text-[#2EF2A5]">{d.firewall.allowed.toLocaleString('de-DE')}</span></div>
              </div>
            </Panel>
          </div>

          <Panel title="Incidents" subtitle="incident management" testid="soc-incidents">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono text-left"><th className="py-2 font-normal">ID</th><th className="py-2 font-normal">Title</th><th className="py-2 font-normal">Severity</th><th className="py-2 font-normal">Owner</th><th className="py-2 font-normal">Age</th><th className="py-2 font-normal">Status</th></tr></thead>
              <tbody className="font-mono text-[13px]">{d.incidents.map((i) => (
                <tr key={i.id} className="border-t border-[color:var(--itw-border)]">
                  <td className="py-2 text-[color:var(--itw-text-3)]">{i.id}</td>
                  <td className="py-2 text-[color:var(--itw-text)]">{i.title}</td>
                  <td className="py-2"><StatusPill status={i.severity} /></td>
                  <td className="py-2 text-[color:var(--itw-text-2)]">{i.owner}</td>
                  <td className="py-2 text-[color:var(--itw-text-3)]">{i.age}</td>
                  <td className="py-2"><StatusPill status={i.status} /></td>
                </tr>
              ))}</tbody>
            </table></div>
          </Panel>
        </div>
      )}
    </DashboardShell>
  );
}
